
import React from 'react';
import ReactDOM from 'react-dom';
import '../../../../styles/actionMenuRenderer.scss';

// Shared utilities
import { MediaOpsService, AlertMessagesService, Utils } from '../../../../shared';

// Feature imports
import { PlacementDetailsTableService } from '../../../placements';
class ActionMenuRenderer extends React.Component {
    constructor(props) {
        super(props);
        this.state = { position: { top: 0, left: 0 } };
        this.buttonRef = React.createRef();
        this.menuRef = React.createRef();
        this.menuContainer = document.createElement('div');
        this.menuContainer.className = 'dropdown-portal-container';
        this.isMenuContainerAppended = false;
    }

    componentDidMount() {
        if (!this.isMenuContainerAppended) {
            document.body.appendChild(this.menuContainer);
            this.isMenuContainerAppended = true;
        }
        document.addEventListener('click', this.handleOutsideClick);
        document.addEventListener('scroll', this.handleOutsideClick, true);
    }

    componentWillUnmount() {
        if (this.isMenuContainerAppended) {
            document.body.removeChild(this.menuContainer);
            this.isMenuContainerAppended = false;
        }
        document.removeEventListener('click', this.handleOutsideClick);
        document.removeEventListener('scroll', this.handleOutsideClick, true);
    }

    handleOutsideClick = (e) => {
        // Only handle clicks if this specific instance's menu is open
        if (!this.props.isOpen) {
            return;
        }

        // ✅ Skip if click inside menu or button
        if (this.buttonRef.current?.contains(e.target) || this.menuRef.current?.contains(e.target)) {
            return;
        }

        this.props.onToggle(null);
    };

    getMenuHeight = () => {
        // If menu is currently rendered and ref exists, get actual height
        if (this.menuRef.current) {
            return this.menuRef.current.offsetHeight;
        }
        // Fallback: estimate based on number of menu items
        const baseHeight = 60; // padding and border
        const itemHeight = 48; // approximate height per menu item
        let itemCount = 3; // base items: Placements, Export placements, Archive campaign

        if (this.props.campaign?.exportStatus?.includes("Error")) {
            itemCount += 1; // Copy error details
        }
        if (this.props.campaign?.nestedData || this.props.campaign?.data_source?.toLowerCase() === 'mediaocean') {
            itemCount += 1; // Unmerge
        }

        return baseHeight + (itemCount * itemHeight);
    };

    toggleMenu = (e) => {
        e.stopPropagation();
        const rect = this.buttonRef.current.getBoundingClientRect();
        const menuHeight = this.getMenuHeight();
        const viewportHeight = window.innerHeight;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceAbove = rect.top;

        // Position above if there's not enough space below and there's more space above
        const shouldPositionAbove = spaceBelow < menuHeight && spaceAbove > spaceBelow;

        const newPosition = {
            top: shouldPositionAbove
                ? rect.top + window.scrollY - menuHeight + 65
                : rect.bottom + window.scrollY,
            left: rect.left + window.scrollX + 60,
        };

        this.setState({
            position: newPosition,
        });

        this.props.onToggle(this.props.campaign?.id); // notify parent
    };

    navigateToPlacementDetails = (campaignObj, event) => {
        this.props.setSpinnerCount(1);
        this.toggleMenu(event);
        // Navigate to the placement details page
        this.props.navigate(campaignObj, 'placementDetailsTable');
    };

    /**
     * Function to handle the export Placement functionality in Actions tab
     * This will call the placement API to get all the placement details and modify that to create an export data for excel
     * @param {object} campaignObj - campaign object
     * sending 'versionOneApi' getplacement call, to find which version of PDMS we are using,then use api as per version
    */
    exportPlacements = (campaignObj, event) => {
        this.props.setSpinnerCount(1);
        this.toggleMenu(event);
        let campaignId = encodeURIComponent(campaignObj.id);
        MediaOpsService.getPlacements(campaignId, null, this.props?.versionOneApi).then((response) => {
            this.props.setSpinnerCount(0);
            if (response.data.length) {
                let exportData = response.data.map((placement) => {
                    return {
                        "Campaign ID": placement.amd_campaignid,
                        "Campaign Name": campaignObj.campaign_name,
                        "Placement ID": placement.placements_id,
                        "Placement Type": placement.placement_type,
                        "Placement Taxonomy": placement.placement_taxonomy,
                        "Flight Start Date": placement.flight_start,
                        "Flight End Date": placement.flight_end,
                        "Export Status": placement.export_status
                    }
                })
                Utils.exportToExcel(exportData, "PlacementsReport", campaignObj.campaign_name + '_PlacementsReport.xlsx')
                AlertMessagesService.showSuccessToastMessage(`Placements exported successfully for ${campaignObj.campaign_name}`);
            } else {
                AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.exportPlacements(campaignObj), title: `Unable to find placement for ${campaignObj.campaign_name}`, bodyMsgArr: [] }]);
            }
        }).catch((err) => {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.exportPlacements(campaignObj), title: 'Unable to export the placement', bodyMsgArr: [] }]);
        })
    }

    /**
     * Function to handle the archive Campaign functionality in Actions tab
     * This will call the campaign API to update the isArchive flag to true
     * @param {object} campaignObj - campaign object
    */
    archiveCampaign = (campaignObj, event) => {
        event?.stopPropagation();
        this.props.setSpinnerCount(1);
        this.toggleMenu(event);
        let payload = { "isArchive": true, ...((campaignObj.parent_id == null || campaignObj.parent_id === '') ? { plannedCampaign: true } : {}) };
        let campaignId = encodeURIComponent(campaignObj.id);
        MediaOpsService.updateCampaign(payload, campaignId, this.props?.versionOneApi).then((response) => {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showSuccessToastMessage(`${campaignObj.campaign_name} has been archived successfully`);
            this.props.fetchCampaignData();
        }).catch(() => {
            this.props.setSpinnerCount(0);
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: () => this.archiveCampaign(campaignObj, event), title: `Unable to archive the ${campaignObj.campaign_name}`, bodyMsgArr: [] }]);
        })
    }

    copyErrorDetails = (errorDetails, event) => {
        this.toggleMenu(event);
        PlacementDetailsTableService.copyTextToClipboard(errorDetails);
    }

    unmergeCampaigns = (campaign, event) => {
        this.props.setSpinnerCount(1);
        this.toggleMenu(event);
        let campaignId = encodeURIComponent(campaign?.id);
        let payload = {};
        if (campaign?.nestedData) {
            payload = {
                parent_synthetic_id: campaign?.synthetic_id,
                parent_id: campaign?.id,
                parent_status: campaign?.export_status,
                child_synthetic_ids: campaign?.nestedData?.filter(child => child.data_source?.toLowerCase() === "mediaocean").map(child => child.synthetic_id)
            };
        } else {
            payload = {
                parent_synthetic_id: campaign?.parentSyntheticId,
                parent_id: campaign?.parent_id,
                parent_status: campaign?.parentStatus,
                child_synthetic_ids: [campaign?.synthetic_id]
            };
        }

        MediaOpsService.unmergeCampaigns(campaignId, payload, this.props?.versionOneApi).then((response) => {
            AlertMessagesService.showSuccessToastMessage('Campaigns are unmerged!');
            this.props.fetchCampaignData();
        }, (error) => {
            AlertMessagesService.showMultipleAlerts([{ type: 'danger', retryBtnText: 'Retry', retryBtnEvent: this.unmergeCampaigns, title: 'Unable to unmerged the campaigns', bodyMsgArr: [] }]);
        }).finally(() => { this.props.setSpinnerCount(0) });
    }

    renderMenu() {
        const { top, left } = this.state.position;
        if (!this.props.isOpen) return null;

        return ReactDOM.createPortal(
            <div className="action-dropdown-portal-menu" style={{ top, left, position: 'absolute', zIndex: 9999 }} ref={this.menuRef}>
                <div className="dropdown-content-actions">
                    <div className="action-card">
                        {this.props.campaign.data_source?.toLowerCase() !== 'planit' && 
                        <>
                            <div className='menu-item' style={{ cursor: 'pointer' }} onClick={this.navigateToPlacementDetails.bind(this, this.props.campaign)} >
                                <span>Placements</span>
                            </div>
                            <div className='menu-item' style={{ cursor: 'pointer' }} onClick={() => { this.props.onToggle(this.props.campaign.id); this.props.onCopyPlacements && this.props.onCopyPlacements(this.props.campaign); }} >
                                <span>Copy Placements</span>
                            </div>
                        </>
                        }
                        <div className='menu-item' style={{ cursor: 'pointer' }} onClick={this.exportPlacements.bind(this, this.props.campaign)}>
                            <span>Export placements</span>
                        </div>
                        <div className='menu-item' style={{ cursor: 'pointer' }} onClick={(e) => { this.archiveCampaign(this.props.campaign, e); }}>
                            <span>Archive campaign</span>
                        </div>
                        {this.props.campaign.exportStatus.includes("Error") &&
                            <div className='menu-item' style={{ cursor: 'pointer' }} onClick={this.copyErrorDetails.bind(this, this.props.campaign.exportStatusError)}>
                                <span>Copy error details</span>
                            </div>
                        }
                        {(this.props.campaign.nestedData || this.props.campaign.data_source?.toLowerCase() === 'mediaocean') && (
                            <div className='menu-item' style={{ cursor: 'pointer' }} onClick={this.unmergeCampaigns.bind(this, this.props.campaign)}>
                                <span>{this.props.campaign.nestedData ? `Unmerge All` : (this.props.campaign.data_source?.toLowerCase() === 'mediaocean' ? `Unmerge` : ``)}</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>,
            this.menuContainer
        );
    }

    render() {
        return (
            <>
                <button slot="invoker" aria-haspopup="true" className="icon medium" ref={this.buttonRef} onClick={this.toggleMenu}>
                    <omni-icon icon-id="omni:interactive:actions"></omni-icon>
                </button>

                {this.renderMenu()}
            </>
        );
    }
}

// Memoize the component to prevent unnecessary re-renders
export default React.memo(ActionMenuRenderer, (prevProps, nextProps) => {
    // Return true when props are equal (to skip re-render)
    // Return false when props differ (to trigger re-render)
    
    // Check campaign properties that are displayed/used in the menu
    const campaignPropsAreEqual =
        prevProps.campaign?.id === nextProps.campaign?.id &&
        prevProps.campaign?.export_status === nextProps.campaign?.export_status &&
        prevProps.campaign?.exportStatus === nextProps.campaign?.exportStatus &&
        prevProps.campaign?.exportStatusError === nextProps.campaign?.exportStatusError &&
        prevProps.campaign?.synthetic_id === nextProps.campaign?.synthetic_id &&
        prevProps.campaign?.campaign_name === nextProps.campaign?.campaign_name &&
        prevProps.campaign?.nestedData === nextProps.campaign?.nestedData &&
        prevProps.campaign?.data_source === nextProps.campaign?.data_source &&
        prevProps.campaign?.parentSyntheticId === nextProps.campaign?.parentSyntheticId &&
        prevProps.campaign?.parent_id === nextProps.campaign?.parent_id &&
        prevProps.campaign?.parentStatus === nextProps.campaign?.parentStatus;

    return (
        prevProps.isOpen === nextProps.isOpen &&
        prevProps.onToggle === nextProps.onToggle &&
        prevProps.navigate === nextProps.navigate &&
        prevProps.setSpinnerCount === nextProps.setSpinnerCount &&
        prevProps.fetchCampaignData === nextProps.fetchCampaignData &&
        prevProps.versionOneApi === nextProps.versionOneApi &&
        campaignPropsAreEqual
    );
});
